import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogoFull } from '../components/Logo';
import LoginGlobePanel from '../components/auth/LoginGlobePanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const showWelcomeToast = (title = 'Welcome to RoomHora!', description = 'Your RoomHora dashboard is ready.') => {
  toast.success(title, {
    description,
    duration: 4800,
    className: 'roomhora-welcome-toast',
  });
};

/**
 * Login page - email + Google.
 *
 * Phone OTP was retired in April 2026. New sign-ups happen through email or
 * Google Sign-In.
 */
export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [googleReady, setGoogleReady] = useState(false); // tracks GSI load
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/dashboard';

  const googleBtnRef = useRef(null);

  // ---------------------------------------------------------------------
  // Google Sign-In
  // ---------------------------------------------------------------------
  const handleGoogleCallback = useCallback(async (response) => {
    if (!response.credential) {
      toast.error('Google sign-in failed. Please try again.');
      return;
    }
    setLoading(true);
    try {
      await loginWithGoogle(response.credential);
      showWelcomeToast('Welcome to RoomHora!', 'Your account is ready. Start exploring verified rentals.');
      navigate(from);
    } catch (error) {
      if (error.isNetworkError) {
        toast.error('Backend is not reachable. Try the demo account: owner@rentroom.com / owner123');
      } else {
        toast.error(error.message || 'Google sign-in failed');
      }
    } finally {
      setLoading(false);
    }
  }, [loginWithGoogle, navigate, from]);

  const renderGoogleButton = useCallback(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google?.accounts?.id || !googleBtnRef.current) return;
    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCallback,
        locale: 'en',
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        locale: 'en',
        width: googleBtnRef.current.offsetWidth || 340,
      });
      setGoogleReady(true);
    } catch (_e) {
      /* already rendered — ignore */
    }
  }, [handleGoogleCallback]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    // If GSI already loaded (e.g. navigating back to /login), render immediately.
    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return;
    }

    if (document.getElementById('google-gsi-script')) return;

    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client?hl=en';
    script.async = true;
    script.defer = true;
    script.onload = renderGoogleButton;
    script.onerror = () => {
      toast.error('Could not load Google Sign-In. Check your internet connection.');
    };
    document.body.appendChild(script);
  }, [renderGoogleButton]);

  // ---------------------------------------------------------------------
  // Email submit
  // ---------------------------------------------------------------------
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(formData.name, formData.email, formData.password);
      }
      if (result?.demo) {
        toast.success('Signed in — demo mode', {
          description:
            'Backend is offline, so you are browsing with a mocked owner session. Deploy the server for live data.',
          duration: 8000,
        });
      } else {
        showWelcomeToast(
          isLogin ? 'Welcome back!' : 'Account created successfully!',
          isLogin
            ? 'Your saved spaces and dashboard are ready.'
            : 'Your RoomHora account is ready to explore.'
        );
      }
      navigate(from);
    } catch (error) {
      if (error.isNetworkError) {
        toast('Try the demo account', {
          description:
            "The backend isn't reachable. Use owner@rentroom.com / owner123 to browse in demo mode.",
          duration: 10000,
        });
      } else {
        toast.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-slate-950 lg:grid lg:grid-cols-[1.06fr_0.94fr]" data-testid="login-page">
      <div className="relative hidden min-h-screen overflow-hidden lg:block">
        <LoginGlobePanel />
      </div>

      <div className="relative flex min-h-screen w-full flex-col justify-center overflow-hidden bg-cream-50 px-6 py-10 sm:px-8 md:px-16 lg:px-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.45]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(14,165,233,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(244,63,94,0.07) 1px, transparent 1px)',
            backgroundSize: '42px 42px',
          }}
        />
        <div className="relative mx-auto w-full max-w-[460px]">
        <div className="mb-8" data-testid="login-logo">
          <LogoFull size="xlarge" />
        </div>

        <h1 className="font-serif text-3xl md:text-4xl lg:text-4xl text-slate-900 mb-2">
          {isLogin ? 'Welcome back' : 'Create your account'}
        </h1>
        <p className="text-base text-slate-500 mb-8">
          {isLogin
            ? 'Sign in to your RoomHora account to continue.'
            : 'Join RoomHora to discover rentals worldwide.'}
        </p>

        <form onSubmit={handleEmailSubmit} className="space-y-4">
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm lg:text-[15px] text-slate-700">
                Full Name
              </Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-12 h-12 rounded-xl text-base"
                  required={!isLogin}
                  data-testid="register-name-input"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm lg:text-[15px] text-slate-700">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                className="pl-12 h-12 rounded-xl text-base"
                required
                data-testid="email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm lg:text-[15px] text-slate-700">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                className="pl-12 pr-12 h-12 rounded-xl text-base"
                required
                minLength={6}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {isLogin && (
              <div className="flex justify-end pt-1">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-sky-600 hover:text-sky-700 hover:underline transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-xl text-base font-medium shadow-[0_8px_30px_-12px_rgba(15,23,42,0.5)] transition-all hover:shadow-[0_18px_38px_-14px_rgba(15,23,42,0.55)]"
            disabled={loading}
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : isLogin ? (
              'Sign In'
            ) : (
              'Create Account'
            )}
          </Button>

          <p className="text-center text-sm text-slate-500">
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-accent font-medium hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-sm text-slate-400">or continue with</span>
          </div>
        </div>

        {/* Google Sign In */}
        {process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
          <div className="w-full mb-3 relative" data-testid="google-signin-btn">
            {/* GSI renders its own iframe button into this div */}
            <div ref={googleBtnRef} className="w-full h-12 flex items-center justify-center" />
            {/* Shown while GSI script is still loading */}
            {!googleReady && (
              <Button
                type="button"
                variant="outline"
                className="absolute inset-0 w-full h-12 rounded-xl border-slate-200 bg-white hover:bg-slate-50 text-base"
                disabled
              >
                <Loader2 className="w-4 h-4 mr-2 animate-spin text-slate-400" />
                <span className="text-slate-400">Loading Google…</span>
              </Button>
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 rounded-xl border-slate-200 hover:bg-slate-50 text-base mb-3"
            onClick={() => toast.info('Google Sign-In is not configured yet. Set REACT_APP_GOOGLE_CLIENT_ID in Vercel.')}
            data-testid="google-signin-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>
        )}

        <p className="mt-6 text-center text-xs text-slate-400">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="underline hover:text-slate-600">
            Terms
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="underline hover:text-slate-600">
            Privacy Policy
          </Link>
          .
        </p>
        </div>
      </div>
    </div>
  );
}
